<script setup>
import { computed,onUnmounted, onMounted, ref } from 'vue'
import { useUserStore } from '../store/user'
import { sendGTagEvent } from '../helpers/gtag'
import { sendEvent } from '@/helpers/cerebro'
import Header from '@/components/molecules/Header.vue'
import DividerWithText from '@/components/molecules/DividerWithText.vue'
import { getRedirectLink } from '@/services/authService'
import { useToast } from 'vue-toast-notification'
import PlayStoreLogo from '@/assets/icons/playstore-logo.svg'
import AppleLogo from '@/assets/icons/apple-logo.svg'
import { getWhichStoreToRedirect } from '@/helpers/page'
import { useOnboardingStore } from '@/store/onboarding'
import { storeToRefs } from 'pinia'

const onboardingStore = useOnboardingStore()
const { QUIZ_ID, steps } = storeToRefs(onboardingStore)

const userStore = useUserStore()
const toast = useToast()

const color = computed(() =>
  getComputedStyle(document.documentElement).getPropertyValue('--neutrals-300').trim()
)

const store = computed(() => getWhichStoreToRedirect())

const email = computed(() => userStore.userData.email)

onMounted(async () => {
  const authUser = await userStore.getCurrentUser()

  sendGTagEvent({ event: 'flow_completed' })

  sendEvent({
    eventName: 'page_displayed',
    properties: {
      page_name: 'payment_success',
      page_number: steps.value.length + 2
    }
  })

  if (userStore.isUserPro()) {
    showProModal.value = true
  } 
})

const downloadClick = async () => {
  sendEvent({
    eventName: 'download_app_clicked',
    properties: { which_store: getWhichStoreToRedirect(), quiz_id: QUIZ_ID.value },
  })
  sendGTagEvent({ event: 'download_app_clicked' })
  let redirectLink
  try {
    redirectLink = userStore.getRedirectLink() ?? (await getRedirectLink())
  } catch (error) {
    toast.error(error)
  }
  const newTab = window.open(redirectLink, '_blank')
  if (!newTab) {
    // Fallback: Open in the same tab
    window.location.href = redirectLink
  }
}

onUnmounted(() => {
  sendEvent({
    eventName: 'onboarding_completed',
    properties: { quiz_id: QUIZ_ID.value }
  })

  sendEvent({
    eventName: 'page_completed',
    properties: {
      page_name: 'payment_success',
      page_number: steps.value.length + 2
    }
  })
})

const showProModal = ref(false)

</script>

<template>
  <Header />
  <div class="container mt-20">
    <div class="success-box">
      <div class="heading-6 confirmation primary-dark">Payment Successful</div>
      <img class="celebrate-image" src="@/assets/images/celebrate.png" alt="celebrating" />

      <p class="heading-5">Welcome to Sparkle!</p>

      <p class="neutral-500">
        We’re so glad you’ve joined us! You now have complete access to our expanding library of
        top-selling books and the best ideas worldwide.
      </p>
      <div class="activation">
        <p class="heading-5">To activate your account:</p>
        <p class="heading-6">Log in automatically</p>
        <p class="neutral-500">
          Tap the button below to download Sparkle, and your account will be logged in
          automatically:
        </p>
        <button class="btn btn-primary btn-lg" @click="downloadClick">
          <component
              :is="store === 'play_store' ? PlayStoreLogo : AppleLogo"
            />
            <span>
              Go to
              {{ store === 'play_store' ? 'Play Store' : 'App Store' }}</span
            >
        </button>
        <DividerWithText :color="color" thickness="1" text="or" />
        <p class="heading-6">Log in manually</p>
        <p class="neutral-500">
          Use <span class="primary-color">{{ email }}</span> and password we have sent to its inbox.
          If it is not there, make sure to check Spam/Junk folder
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.container {
  padding-bottom: 40px;
  max-height: calc(100vh - 93px);
  overflow: scroll;
}

.checking-box {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
  margin-top: 50px;
}

.success-box {
  max-width: 480px;
  margin: 0 auto;
  padding: 8px;
  border-radius: 16px;
  border: 1px solid var(--primary-light);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.neutral-500 {
  color: var(--neutrals-500);
}

img {
  max-width: 100%;
}

.confirmation {
  background: #f5af2a;
  padding: 24px;
  border-radius: 16px 16px 0 0;
  margin: -8px -8px 16px -8px;
}

.activation {
  background: #f5af2a33;
  border: 1px solid #f5af2a;
  width: Fill (343px) px;
  padding: 32px 12px 32px 12px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  border-radius: 12px;
}

.celebrate-image {
  width: auto;
  height: 330px;
  object-fit: contain;
}

.btn {
  display: flex;
  justify-content: center;
}
</style>
